import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedAdminRoute from './components/auth/ProtectedAdminRoute';
import ProtectedDriverRoute from './components/auth/ProtectedDriverRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import DriverLayout from './pages/driver/layouts/DriverLayout';

// Eager Load Critical Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

// Lazy Load Functional Modules
const ProductListPage = lazy(() => import('./pages/products/ProductListPage'));
const ProductForm = lazy(() => import('./pages/products/ProductFormPage'));
const ReviewManagementPage = lazy(() => import('./pages/reviews/ReviewManagementPage'));
const CategoryList = lazy(() => import('./pages/categories/CategoryList'));
const CategoryForm = lazy(() => import('./pages/categories/CategoryForm'));
const SupplierListPage = lazy(() => import('./pages/suppliers/SupplierListPage'));
const SupplierFormPage = lazy(() => import('./pages/suppliers/SupplierFormPage'));
const IngredientListPage = lazy(() => import('./pages/ingredients/IngredientListPage'));
const IngredientFormPage = lazy(() => import('./pages/ingredients/IngredientFormPage'));
const PurchaseOrderListPage = lazy(() => import('./pages/procurement/PurchaseOrderListPage'));
const PurchaseOrderFormPage = lazy(() => import('./pages/procurement/PurchaseOrderFormPage'));
const ProductionRunPage = lazy(() => import('./pages/production/ProductionRunPage'));
const KitchenBoard = lazy(() => import('./pages/kitchen/KitchenBoard').then(module => ({ default: module.KitchenBoard })));
const QCPackingPage = lazy(() => import('./pages/QCPackingPage').then(module => ({ default: module.QCPackingPage })));
const DispatchBoard = lazy(() => import('./pages/dispatch/DispatchBoard'));
const LogisticsPage = lazy(() => import('./pages/LogisticsPage').then(module => ({ default: module.LogisticsPage })));
const LiveMap = lazy(() => import('./pages/admin/logistics/LiveMap'));
const OrdersDeepDive = lazy(() => import('./pages/orders/deep-dive/OrdersDeepDive'));
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage'));
const ActionRequiredPage = lazy(() => import('./pages/orders/ActionRequiredPage'));
const InventoryDeepDive = lazy(() => import('./pages/inventory/deep-dive/InventoryDeepDive').then(module => ({ default: module.InventoryDeepDive })));
const AnalyticsDeepDive = lazy(() => import('./pages/analytics/deep-dive/AnalyticsDeepDive').then(module => ({ default: module.AnalyticsDeepDive })));
const UsersDeepDive = lazy(() => import('./pages/users/deep-dive/UsersDeepDive').then(module => ({ default: module.UsersDeepDive })));
const UserDetail = lazy(() => import('./pages/users/UserDetail'));
const TransactionsPage = lazy(() => import('./pages/transactions/TransactionsPage'));
const SettlementReconciliationPage = lazy(() => import('./pages/finance/SettlementReconciliationPage'));
const Reports = lazy(() => import('./pages/reports/ReportsPage'));
const ProductionReportPage = lazy(() => import('./pages/reports/ProductionReportPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const Team = lazy(() => import('./pages/settings/Team'));
const DatabasePage = lazy(() => import('./pages/system/Database'));
const PrintManifestPage = lazy(() => import('./pages/PrintManifestPage'));
const HaccpSurveillanceCockpit = lazy(() => import('./pages/food-safety/HaccpSurveillanceCockpit'));
const LabelPrintPage = lazy(() => import('./pages/print/LabelPrintPage').then(module => ({ default: module.LabelPrintPage })));
const ActiveProductionPage = lazy(() => import('./pages/production/ActiveProductionPage'));
const DriverDashboard = lazy(() => import('./pages/driver/DriverDashboard'));
const JobDetails = lazy(() => import('./pages/driver/JobDetails'));
const TrackingPage = lazy(() => import('./pages/public/TrackingPage')); // WO-807 Ticket 6
const IntakePage = lazy(() => import('./pages/omnichannel/IntakePage'));
const OmnichannelDrafts = lazy(() => import('./pages/OmnichannelDrafts'));
const TriageQueuePage = lazy(() => import('./pages/omnichannel/TriageQueuePage'));
const DraftReviewPage = lazy(() => import('./pages/DraftReviewPage'));
const AiFlywheelPage = lazy(() => import('./pages/admin/ai/AiFlywheelPage'));
const InventoryAnomaliesPage = lazy(() => import('./pages/inventory/InventoryAnomaliesPage'));

// EPIC-OPS-003: Sovereign Admin Copilot
import { CopilotProvider } from './context/CopilotContext';
import CopilotFab from './components/copilot/CopilotFab';
const SovereignAdminCopilot = lazy(() => import('./components/copilot/SovereignAdminCopilot'));

// Loading Fallback
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
  </div>
);

const App: React.FC = () => {
  return (
    <CopilotProvider>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* PUBLIC ROUTE */}
        <Route path="/login" element={<Login />} />
        {/* WO-807: Ticket 6 - Magic Link Tracking */}
        <Route path="/track/:hash" element={<TrackingPage />} />

        {/* DRIVER APP ROUTES (Protocol A & B) */}
        <Route path="/driver" element={
          <ProtectedDriverRoute>
            <DriverLayout />
          </ProtectedDriverRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DriverDashboard />} />
          <Route path="jobs/:id" element={<JobDetails />} />
        </Route>

        {/* PROTECTED DASHBOARD ROUTES */}
        <Route element={<ProtectedAdminRoute><DashboardLayout /></ProtectedAdminRoute>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/products" element={<ProductListPage />} />
          <Route path="/products/new" element={<ProductForm />} />
          <Route path="/products/:id" element={<ProductForm />} />

          <Route path="/reviews" element={<ReviewManagementPage />} />

          <Route path="/categories" element={<CategoryList />} />
          <Route path="/categories/new" element={<CategoryForm />} />
          <Route path="/categories/:id" element={<CategoryForm />} />

          <Route path="/suppliers" element={<SupplierListPage />} />
          <Route path="/suppliers/new" element={<SupplierFormPage />} />
          <Route path="/suppliers/:id" element={<SupplierFormPage />} />

          <Route path="/ingredients" element={<IngredientListPage />} />
          <Route path="/ingredients/new" element={<IngredientFormPage />} />
          <Route path="/ingredients/:id" element={<IngredientFormPage />} />

          <Route path="/purchase-orders" element={<PurchaseOrderListPage />} />
          <Route path="/purchase-orders/new" element={<PurchaseOrderFormPage />} />
          <Route path="/purchase-orders/:id" element={<PurchaseOrderFormPage />} />

          <Route path="/inventory/production" element={<ProductionRunPage />} />



          {/* WO-803: Active Production */}
          <Route path="/production/active" element={<ActiveProductionPage />} />

          {/* WO-801: KDS Board */}
          <Route path="/kitchen" element={<KitchenBoard />} />

          {/* WO-806: QC & Packing Station */}
          <Route path="/qc-packing" element={<QCPackingPage />} />

          {/* STORY-QC-001.6: Food Safety Surveillance Cockpit */}
          <Route path="/admin/food-safety/haccp" element={<HaccpSurveillanceCockpit />} />
          <Route path="/food-safety/haccp" element={<HaccpSurveillanceCockpit />} />

          {/* WO-1001: Dispatch Command Center (Station C) */}
          <Route path="/dispatch" element={<ProtectedAdminRoute><DispatchBoard /></ProtectedAdminRoute>} />

          {/* Logistics Command Center (Station D) */}
          <Route path="/logistics" element={<LogisticsPage />} />

          {/* WO-807: The Watchtower */}
          <Route path="/logistics/map" element={<LiveMap />} />

          <Route path="/action-required" element={<ActionRequiredPage />} />
          <Route path="/orders" element={<OrdersDeepDive />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/inventory" element={<InventoryDeepDive />} />
          <Route path="/admin/inventory/anomalies" element={<InventoryAnomaliesPage />} />
          <Route path="/inventory/anomalies" element={<InventoryAnomaliesPage />} />
          <Route path="/analytics" element={<AnalyticsDeepDive />} />

          <Route path="/users" element={<UsersDeepDive />} />
          <Route path="/users/:id" element={<UserDetail />} />

          {/* Super Admin Isolated Routes (STORY-RBAC-002) */}
          <Route path="/omnichannel/intake" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <IntakePage />
            </RoleProtectedRoute>
          } />
          <Route path="/omnichannel/triage" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <TriageQueuePage />
            </RoleProtectedRoute>
          } />
          <Route path="/triage" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <TriageQueuePage />
            </RoleProtectedRoute>
          } />
          <Route path="/draft-review" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <DraftReviewPage />
            </RoleProtectedRoute>
          } />
          <Route path="/admin/draft-review" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <DraftReviewPage />
            </RoleProtectedRoute>
          } />
          <Route path="/admin/drafts" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <DraftReviewPage />
            </RoleProtectedRoute>
          } />
          <Route path="/admin/ai-flywheel" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <AiFlywheelPage />
            </RoleProtectedRoute>
          } />
          <Route path="/flywheel" element={
            <RoleProtectedRoute allowedRoles={['super_admin']}>
              <AiFlywheelPage />
            </RoleProtectedRoute>
          } />

          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/finance/settlements" element={<SettlementReconciliationPage />} />

          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/production" element={<ProductionReportPage />} />

          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/team" element={<Team />} />
          <Route path="/system/database" element={<DatabasePage />} />
        </Route>

        {/* Standalone Print Route (Authenticated but no Layout) */}
        <Route path="/print/manifest" element={<ProtectedAdminRoute><PrintManifestPage /></ProtectedAdminRoute>} />

        {/* WO-807: Thermal Label Print */}
        <Route path="/print/label/:id" element={<ProtectedAdminRoute><LabelPrintPage /></ProtectedAdminRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CopilotFab />
      <SovereignAdminCopilot />
    </Suspense>
  </CopilotProvider>
  );
};

export default App;